/**
 * Statistical utilities for box plot calculations
 */

/**
 * Calculate a quantile (percentile) from a sorted array
 * @param sortedArray - Array of numbers sorted in ascending order
 * @param q - Quantile value between 0 and 1 (e.g., 0.25 for Q1, 0.5 for median)
 * @returns The quantile value
 */
function quantile(sortedArray: number[], q: number): number {
  if (sortedArray.length === 0) return 0;
  if (sortedArray.length === 1) return sortedArray[0];

  const pos = (sortedArray.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;

  if (sortedArray[base + 1] !== undefined) {
    return sortedArray[base] + rest * (sortedArray[base + 1] - sortedArray[base]);
  } else {
    return sortedArray[base];
  }
}

/**
 * Box plot data structure
 */
export type BoxPlotData = {
  reference: string;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  outliers: number[];
  count: number;
  mean: number;
};

/**
 * Calculate box plot statistics for a set of values
 * @param values - Array of numeric values
 * @returns Box plot statistics
 */
export function calculateBoxPlotStats(values: number[]): Omit<BoxPlotData, 'reference'> {
  if (values.length === 0) {
    return {
      min: 0,
      q1: 0,
      median: 0,
      q3: 0,
      max: 0,
      outliers: [],
      count: 0,
      mean: 0,
    };
  }

  const sortedValues = [...values].sort((a, b) => a - b);

  const q1 = quantile(sortedValues, 0.25);
  const median = quantile(sortedValues, 0.5);
  const q3 = quantile(sortedValues, 0.75);
  const iqr = q3 - q1;

  // Calculate whisker boundaries (1.5 * IQR rule)
  const lowerBound = q1 - 1.5 * iqr;
  const upperBound = q3 + 1.5 * iqr;

  // Find actual min/max within whisker boundaries
  const min = sortedValues.find((v) => v >= lowerBound) ?? sortedValues[0];
  const max =
    [...sortedValues].reverse().find((v) => v <= upperBound) ??
    sortedValues[sortedValues.length - 1];

  // Identify outliers
  const outliers = sortedValues.filter((value) => value < lowerBound || value > upperBound);

  // Calculate mean
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;

  return {
    min,
    q1,
    median,
    q3,
    max,
    outliers,
    count: values.length,
    mean,
  };
}
